/**
 * Settings → Screening (company_admin+; the route is wrapped in <RequireRole>).
 * One tab per role; each tab edits that role's questions and rules in a draft
 * kept per role (switching tabs loses nothing). Save validates, then PUTs
 * with `draft: false` — saving IS the review. A `beforeunload` prompt guards
 * unsaved edits against closing or reloading the tab.
 */
import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { errorMessage, useToast } from '@/components/Toast';
import { usePageTitle } from '@/lib/page-title';
import { useRoles } from '@/features/pipeline/api';
import { QuestionsEditor } from './QuestionsEditor';
import { RulesEditor } from './RulesEditor';
import { useSaveScreeningConfig, useScreeningConfigs } from './screening-api';
import { TryItPanel } from './TryItPanel';
import { EMPTY_CONFIG, type ScreeningConfig } from './types';
import { hasConfigErrors, NO_CONFIG_ERRORS, validateConfig, type ConfigErrors } from './validate';

const frame = 'max-w-[1100px] px-4 py-5 sm:px-6 sm:py-6';

export default function ScreeningSettingsPage() {
  usePageTitle('Screening');
  const rolesQ = useRoles();
  const configsQ = useScreeningConfigs();
  const save = useSaveScreeningConfig();
  const toast = useToast();
  const [slug, setSlug] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, ScreeningConfig>>({});
  const [errors, setErrors] = useState<Record<string, ConfigErrors>>({});

  const roles = (rolesQ.data ?? []).filter((r) => r.slug !== 'unmapped');
  const active = roles.find((r) => r.slug === slug) ?? roles[0] ?? null;
  const dirtyCount = Object.keys(drafts).length;

  useEffect(() => {
    if (dirtyCount === 0) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirtyCount]);

  if (rolesQ.isPending || configsQ.isPending) {
    return (
      <div className={frame}>
        <PageHeader title="Screening" />
        <div className="space-y-3" aria-busy="true" aria-label="Loading screening settings">
          {[0, 1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-white/70" />)}
        </div>
      </div>
    );
  }
  if (rolesQ.isError || configsQ.isError || !configsQ.data || !active) {
    const err = rolesQ.error ?? configsQ.error;
    return (
      <div className={frame}>
        <PageHeader title="Screening" />
        <p role="alert" className="text-sm text-danger">Could not load screening settings{err ? `: ${errorMessage(err)}` : '.'}</p>
        <button type="button" onClick={() => { void rolesQ.refetch(); void configsQ.refetch(); }} className="mt-3 inline-flex min-h-10 items-center rounded-md border border-gray-200 bg-white px-3 text-sm font-medium hover:bg-page">Try again</button>
      </div>
    );
  }

  const saved = configsQ.data[active.slug] ?? EMPTY_CONFIG;
  const current = drafts[active.slug] ?? saved;
  const dirty = active.slug in drafts;
  const errs = errors[active.slug] ?? NO_CONFIG_ERRORS;
  const edit = (patch: Partial<ScreeningConfig>) => setDrafts({ ...drafts, [active.slug]: { ...current, ...patch } });
  const dropDraft = () => {
    const { [active.slug]: _d, ...restDrafts } = drafts;
    const { [active.slug]: _e, ...restErrors } = errors;
    setDrafts(restDrafts);
    setErrors(restErrors);
  };

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validateConfig(current);
    setErrors({ ...errors, [active.slug]: found });
    if (hasConfigErrors(found)) {
      toast.push({ kind: 'error', title: 'Fix the highlighted fields', message: 'Nothing was saved.' });
      return;
    }
    const config: ScreeningConfig = { ...current, draft: false, questions: current.questions.map((q) => ({ ...q, key: q.key.trim(), prompt: q.prompt.trim(), ...(q.type === 'choice' ? { choices: (q.choices ?? []).map((c) => c.trim()).filter(Boolean) } : { choices: undefined }) })) };
    save.mutate(
      { roleSlug: active.slug, config },
      {
        onSuccess: () => { dropDraft(); toast.push({ kind: 'success', title: `${active.title} screening saved` }); },
        onError: (err) => toast.push({ kind: 'error', title: 'Could not save', message: errorMessage(err) }),
      },
    );
  };

  return (
    <form onSubmit={onSave} className={frame} aria-label="Screening settings">
      <PageHeader
        title="Screening"
        subtitle="What the recruiting assistant asks on the phone, and the rules that turn answers into Qualified, Needs review or Not qualified. People make the final call."
        actions={
          <>
            {dirty && (
              <button type="button" onClick={dropDraft} className="inline-flex min-h-10 items-center rounded-md px-3 text-sm font-medium text-gray-700 hover:bg-white">Discard</button>
            )}
            <button type="submit" disabled={!dirty || save.isPending} className="inline-flex min-h-10 items-center rounded-md bg-navy px-3.5 text-sm font-medium text-white hover:bg-navy-dark disabled:opacity-50">
              {save.isPending ? 'Saving…' : `Save ${active.title}`}
            </button>
          </>
        }
      />

      <div role="tablist" aria-label="Role" className="-mx-4 mb-4 flex gap-1 overflow-x-auto border-b border-gray-200 px-4 sm:mx-0 sm:px-0">
        {roles.map((r) => {
          const selected = r.slug === active.slug;
          const unsaved = r.slug in drafts;
          const isDraft = (configsQ.data[r.slug] ?? EMPTY_CONFIG).draft;
          return (
            <button
              key={r.slug}
              role="tab"
              type="button"
              id={`role-tab-${r.slug}`}
              aria-selected={selected}
              aria-controls="role-panel"
              onClick={() => setSlug(r.slug)}
              className={`-mb-px inline-flex min-h-10 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 text-[13px] font-medium ${selected ? 'border-navy text-navy' : 'border-transparent text-muted hover:text-gray-900'}`}
            >
              {r.title}
              {unsaved ? <span className="text-[12px] text-warning-text" title="Unsaved changes">● <span className="sr-only">unsaved changes</span></span>
                : isDraft ? <span className="rounded bg-warning-bg px-1.5 py-0.5 text-[12px] font-medium text-warning-text">Draft</span> : null}
            </button>
          );
        })}
      </div>

      <div id="role-panel" role="tabpanel" aria-labelledby={`role-tab-${active.slug}`} className="space-y-4">
        {saved.draft && (
          <div role="status" className="flex items-start gap-2.5 rounded-xl border border-warning/30 bg-warning-bg px-4 py-3 text-[13px] text-gray-900" data-testid="draft-banner">
            <div>
              <div className="font-medium text-warning-text">Draft — review these questions and rules</div>
              <div className="mt-0.5 text-gray-700">These are starting suggestions for {active.title}. Edit what you like, try a few sample answers below, then Save to put them in use.</div>
            </div>
          </div>
        )}
        <QuestionsEditor questions={current.questions} errors={errs.questions} onChange={(questions) => edit({ questions })} />
        <RulesEditor rules={current.rules} questions={current.questions} errors={errs} onChange={(rules) => edit({ rules })} />
        <TryItPanel config={current} />
      </div>
    </form>
  );
}
